import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import Cookies from 'js-cookie';
import moment from 'moment';
import ReactGA from 'react-ga4';
import { injectIntl } from 'react-intl';

import {
  setAppVersion,
  setAppUpdateAvailable,
} from './actions/App';
import {
  fetchUserProfile,
  refreshTokenIfNecessary,
  logout,
} from './actions/Auth';
import { setSidebarCollectionOpen } from './actions/Collections';

import { isLoggedIn } from './api/Auth';

import ModalRoot from './components/ModalRoot';
import ModalTopRoot from './components/ModalTopRoot';
import UpgradeModal from './components/UpgradeModal';
import ReloadOnShareExit from './components/ReloadOnShareExit';

import {
  getLastActivity,
  setLastActivity,
  setVisitorId,
} from './helpers/Auth';
import { addFlashMessage } from './helpers/FlashMessages';
import { initNotificationChannels } from './helpers/Notifications';
import {
  getUserSetting,
  getSetting,
} from './helpers/Settings';

import ErrorBoundary from './components/ErrorBoundary';
import LoadingMain from './components/LoadingMain';

import version from '../../../version.json';

class App extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      refreshing: false,
    };
  }

  componentDidMount() {
    const { dispatch } = this.props;

    this.refreshAccessTokenIfNecessary();

    setInterval(() => this.checkUserInactivity(), 1000 * 60);

    const subdomain = window.location.hostname.split('.').slice(0, -2).join('.');
    if (process.env.MIX_GA4_MEASUREMENT_ID && subdomain !== 'admin' && !window.sp.do_not_track) {
      ReactGA.initialize(process.env.MIX_GA4_MEASUREMENT_ID, {
        gaOptions: {
          sp_domain: window.location.hostname,
        },
      });
    }

    setVisitorId();

    dispatch(setAppVersion(version));
  }

  componentDidUpdate(prevProps) {
    const {
      intl,
      dispatch,
      profile,
      appVersion,
    } = this.props;
    const { profile: prevProfile } = prevProps;

    if (!profile) {
      this.refreshAccessTokenIfNecessary();
    } else if (!prevProfile) {
      const sidebarOpen = getUserSetting('sidebar_collection_open');
      if (sidebarOpen) {
        dispatch(setSidebarCollectionOpen(true));
      }
    } else {
      setLastActivity();
    }

    if (profile && profile.uuid && (!prevProfile || profile.uuid !== prevProfile.uuid)) {
      initNotificationChannels();
    }

    if (appVersion && appVersion.version && window.sp.version) {
      dispatch(setAppUpdateAvailable(appVersion.version !== window.sp.version));
      if (appVersion.version !== window.sp.version) {
        addFlashMessage({
          id: 'update-notification',
          type: 'update',
          text: intl.formatMessage({
            defaultMessage: 'A new Stockpress version is available. <link>Reload</link>',
          }, {
            link: (msg) => (
              <a href="#reload" onClick={(e) => { e.preventDefault(); window.location.reload(); }}>
                {msg}
              </a>
            ),
          }),
        });
      }
    }
  }

  checkUserInactivity() {
    const {
      dispatch,
      profile,
      uploadModal,
    } = this.props;
    const lastActivity = getLastActivity();

    if (isLoggedIn() && profile && !uploadModal && lastActivity) {
      const sessionTTL = getSetting('session_inactivity_time');
      if (sessionTTL > 0) {
        const invalidateTime = lastActivity.add(sessionTTL, 'seconds').diff(moment());
        if (invalidateTime < 0) {
          dispatch(logout());
        }
      }
    }
  }

  refreshAccessTokenIfNecessary(async = false) {
    const {
      dispatch,
      location,
      profile,
      profileLoading,
    } = this.props;

    if (moment().add(1, 'minutes').unix() > Cookies.get('sp_expiration')) {
      window.sp.isTokenRefreshing = true;
      if (async) {
        dispatch(refreshTokenIfNecessary()).then(() => {
          window.sp.isTokenRefreshing = false;
        });
      } else {
        this.setState({ refreshing: true }, () => {
          dispatch(refreshTokenIfNecessary()).then((success) => {
            window.sp.isTokenRefreshing = false;
            this.setState({ refreshing: false }, () => {
              if (success) {
                dispatch(fetchUserProfile());
              }
            });
          });
        });
      }
    } else if (
      !profile &&
      !profileLoading &&
      location.pathname.indexOf('/auth/') < 0
    ) {
      dispatch(fetchUserProfile());
    }
  }

  render() {
    const { children } = this.props;
    const { refreshing } = this.state;

    if (refreshing) {
      return (
        <LoadingMain />
      );
    }

    return (
      <ErrorBoundary>
        <ReloadOnShareExit />
        {children}
        <ModalRoot />
        <ModalTopRoot />
        <UpgradeModal />
      </ErrorBoundary>
    );
  }
}

const mapStateToProps = state => ({
  appVersion: state.App.version,
  profile: state.Auth.profile,
  profileLoading: state.Auth.loading,
  uploadModal: state.Files.uploadModal,
});

App.propTypes = {
  intl: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  appVersion: PropTypes.object,
  location: PropTypes.object.isRequired,
  profile: PropTypes.object,
  profileLoading: PropTypes.bool.isRequired,
  uploadModal: PropTypes.bool.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

App.defaultProps = {
  appVersion: null,
  profile: null,
};

export default withRouter(connect(mapStateToProps)(injectIntl(App)));
